import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { cloneList, DataModel, DataModelBuilder, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { EventBookingProtocol } from './event-booking-protocol.js';
import { EventBookingStrategy } from './event-booking-strategy.js';

export class TicketAsPassEventBookingProtocol extends EventBookingProtocol {
  static readonly TYPE_ID: string = '582e401d109843aebe0cc2330af1f4a0';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly strategy: EventBookingStrategy = EventBookingStrategy.TicketAsPass;

  private constructor(
    public readonly passTemplateIds: string[],
    public readonly shouldSendElectronicPass: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketAsPassEventBookingProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, TicketAsPassEventBookingProtocol.SCHEMA_VERSION);
  }

  static create(args: {
    passTemplateIds: string[],
    shouldSendElectronicPass?: boolean,
  }): TicketAsPassEventBookingProtocol {
    return new TicketAsPassEventBookingProtocol(
        args.passTemplateIds,
        args.shouldSendElectronicPass ?? true,
    );
  }

  static legacy(args: {
    passTemplateIds?: string[],
    shouldSendElectronicPass?: boolean,
  }): TicketAsPassEventBookingProtocol {
    return new TicketAsPassEventBookingProtocol(
        args.passTemplateIds ?? [],
        args.shouldSendElectronicPass ?? true,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketAsPassEventBookingProtocol {
    return new TicketAsPassEventBookingProtocol(
        MapUtils.getList(map, 'passTemplateIds', (v) => v as string),
        MapUtils.getBoolean(map, 'shouldSendElectronicPass'),
    );
  }

  toMap(_context: SerializationContext): SerializedData {
    return {
      'strategy': fromEnumAsString(this.strategy),
      'passTemplateIds': this.passTemplateIds,
      'shouldSendElectronicPass': this.shouldSendElectronicPass,
    };
  }

  clone(): this {
    return new TicketAsPassEventBookingProtocol(
        cloneList(this.passTemplateIds),
        this.shouldSendElectronicPass,
    ) as this;
  }

  copyWith(data: Partial<TicketAsPassEventBookingProtocol>): this {
    return new TicketAsPassEventBookingProtocol(
        data.passTemplateIds ?? cloneList(this.passTemplateIds),
        data.shouldSendElectronicPass ?? this.shouldSendElectronicPass,
    ) as this;
  }

  isSame(other: TicketAsPassEventBookingProtocol): boolean {
    if(this === other) {
      return true;
    }

    return other.strategy === this.strategy &&
      isSameList(other.passTemplateIds, this.passTemplateIds) &&
      other.shouldSendElectronicPass === this.shouldSendElectronicPass;
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof TicketAsPassEventBookingProtocol;
  }

  static assertType(order: DataModel): order is TicketAsPassEventBookingProtocol {
    return order instanceof TicketAsPassEventBookingProtocol;
  }
}

export class TicketAsPassEventBookingProtocolBuilder extends DataModelBuilder<TicketAsPassEventBookingProtocol> {
  build(context: SerializationContext, map: SerializedData): TicketAsPassEventBookingProtocol {
    return TicketAsPassEventBookingProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketAsPassEventBookingProtocol.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TicketAsPassEventBookingProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the `passTemplateIds` field of type `string[]` was introduced
    if(version < 2) {
      map['passTemplateIds'] = [];
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
